import React, { useEffect, useRef, useState } from "react";
import {
    makeStyles,
    Button,
    TextField,
    Typography,
    Box,
    Card,
    CardContent,
    InputAdornment,
    Avatar,
    MenuItem,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
    Email,
    Lock,
    Business,
    ArrowBack,
} from "@material-ui/icons";
import { withRouter, Link, useHistory, useLocation } from "react-router-dom";
import logoImg from "../assets/images/logo.png";
import { companySignup as signup } from "../actions/auth";
import { generateStrongPassword } from "../utils/passwordGenerator";
import Catagory from "../actions/Catagory";
import { getChildCategories } from "../utils/categoryTree";

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
                "radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)",
            animation: "$float 20s ease-in-out infinite",
        },
    },
    "@keyframes float": {
        "0%, 100%": { transform: "translate(0, 0)" },
        "50%": { transform: "translate(20px, 20px)" },
    },
    container: {
        position: "relative",
        zIndex: 1,
        padding: theme.spacing(4),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
    },
    logoContainer: {
        marginBottom: theme.spacing(3),
        animation: "$fadeInDown 0.8s ease-out",
    },
    backButton: {
        alignSelf: "flex-start",
        marginBottom: theme.spacing(1),
        color: "#667eea",
        textTransform: "none",
        fontWeight: 600,
    },
    "@keyframes fadeInDown": {
        from: {
            opacity: 0,
            transform: "translateY(-30px)",
        },
        to: {
            opacity: 1,
            transform: "translateY(0)",
        },
    },
    logo: {
        maxHeight: 80,
        filter: "drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))",
    },
    card: {
        maxWidth: 980,
        width: "100%",
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(20px)",
        borderRadius: theme.spacing(3),
        boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
        padding: theme.spacing(4),
        animation: "$fadeInUp 0.8s ease-out",
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(2.5),
        },
    },
    "@keyframes fadeInUp": {
        from: {
            opacity: 0,
            transform: "translateY(30px)",
        },
        to: {
            opacity: 1,
            transform: "translateY(0)",
        },
    },
    title: {
        fontWeight: 700,
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        marginBottom: theme.spacing(1),
        textAlign: "center",
    },
    subtitle: {
        textAlign: "center",
        color: "#6b7280",
        marginBottom: theme.spacing(4),
    },
    formSection: {
        marginTop: theme.spacing(3),
    },
    formGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: theme.spacing(2),
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "1fr",
        },
    },
    textField: {
        marginBottom: 0,
        "& .MuiOutlinedInput-root": {
            "&:hover fieldset": {
                borderColor: "#667eea",
            },
            "&.Mui-focused fieldset": {
                borderColor: "#667eea",
            },
        },
    },
    fullSpan: {
        gridColumn: "1 / -1",
    },
    companyDetail: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: theme.spacing(2),
        background: "rgba(102, 126, 234, 0.05)",
        borderRadius: theme.spacing(2),
        marginBottom: 0,
        textAlign: "center",
        animation: "$fadeIn 0.5s ease-out",
    },
    "@keyframes fadeIn": {
        from: { opacity: 0 },
        to: { opacity: 1 },
    },
    companyAvatar: {
        width: theme.spacing(8),
        height: theme.spacing(8),
        marginBottom: theme.spacing(1),
        backgroundColor: "#667eea",
    },
    submitButton: {
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "#fff",
        padding: theme.spacing(1.5, 4),
        borderRadius: theme.spacing(3),
        fontWeight: 600,
        textTransform: "none",
        fontSize: "1rem",
        marginTop: theme.spacing(2),
        width: "100%",
        "&:hover": {
            background: "linear-gradient(135deg, #5568d3 0%, #6a4190 100%)",
            transform: "translateY(-2px)",
            boxShadow: "0 10px 20px rgba(102, 126, 234, 0.3)",
        },
        transition: "all 0.3s ease",
    },
    passwordTools: {
        display: "flex",
        gap: theme.spacing(1),
        alignItems: "center",
        flexWrap: "wrap",
    },
    miniButton: {
        border: "none",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "#fff",
        borderRadius: 8,
        padding: "0.45rem 0.7rem",
        cursor: "pointer",
        fontWeight: 600,
        fontSize: "0.78rem",
    },
    generatedBox: {
        width: "100%",
        background: "rgba(102, 126, 234, 0.08)",
        border: "1px solid rgba(102, 126, 234, 0.3)",
        borderRadius: 10,
        padding: "0.6rem 0.75rem",
        fontSize: "0.82rem",
        color: "#374151",
        wordBreak: "break-all",
    },
    generatedActions: {
        display: "flex",
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
}));

const CompanySignup = () => {
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();
    const { viewMainCatagories, viewSubCatagories } = Catagory();
    const viewMainCatagoriesRef = useRef(viewMainCatagories);
    const viewSubCatagoriesRef = useRef(viewSubCatagories);
    viewMainCatagoriesRef.current = viewMainCatagories;
    viewSubCatagoriesRef.current = viewSubCatagories;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const [catagories, setCatagories] = useState([]);
    const [subCatagories, setSubCatagories] = useState([]);

    const [formData, setFormData] = useState({
        name: "",
        description: "",
        email: "",
        password: "",
        confirmPassword: "",
        logoFile: null,
        logoPreview: "",
        web: "",
        pobox: "",
        woreda: "",
        city: "",
        subCity: "",
        state: "",
        kebele: "",
        street: "",
        phone: "",
        officePhone: "",
        fax: "",
        catagoryId: "",
        subCatagoryId: "",
        customSubCategory: "",
        licenceFile: null,
        licenceName: "",
    });
    const [generatedPassword, setGeneratedPassword] = useState("");

    const {
        name,
        description,
        email,
        password,
        confirmPassword,
        logoFile,
        logoPreview,
        web,
        pobox,
        woreda,
        city,
        subCity,
        state,
        kebele,
        street,
        phone,
        officePhone,
        fax,
        catagoryId,
        subCatagoryId,
        customSubCategory,
        licenceFile,
        licenceName,
    } = formData;

    const numericOnlyFields = new Set([
        "pobox",
        "woreda",
        "phone",
        "officePhone",
        "fax",
    ]);
    const textOnlyFields = new Set([
        "city",
        "subCity",
        "state",
        "kebele",
    ]);

    const handleChange = (field) => (event) => {
        let value = event.target.value;
        if (numericOnlyFields.has(field)) {
            value = value.replace(/[^0-9]/g, "");
        }
        if (textOnlyFields.has(field)) {
            value = value.replace(/[^a-zA-Z\s]/g, "");
        }
        setFormData({ ...formData, [field]: value });
        setError("");
    };

    const handleLogoChange = (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;
        const preview = URL.createObjectURL(file);
        setFormData((prev) => ({ ...prev, logoFile: file, logoPreview: preview }));
    };

    const handleLicenceChange = (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;
        setFormData((prev) => ({ ...prev, licenceFile: file, licenceName: file.name }));
    };

    const isOthersCategoryName = (value) => {
        const normalized = String(value || "").trim().toLowerCase();
        return normalized === "other" || normalized === "others";
    };

    const selectedMainCategory = catagories.find((item) => item.Id === catagoryId) || null;
    const isOtherCategory = Boolean(
        selectedMainCategory &&
        isOthersCategoryName(selectedMainCategory.name)
    );
    const sortedCatagories = [...catagories].sort((a, b) => {
        const aIsOthers = isOthersCategoryName(a?.name);
        const bIsOthers = isOthersCategoryName(b?.name);
        if (aIsOthers && !bIsOthers) return 1;
        if (!aIsOthers && bIsOthers) return -1;
        return String(a?.name || "").localeCompare(String(b?.name || ""));
    });
    const sortCategoriesByName = (items) =>
        [...(items || [])].sort((a, b) =>
            String(a?.name || "").localeCompare(String(b?.name || ""))
        );

    const handleCategoryChange = async (event) => {
        const nextCategoryId = event.target.value;
        setFormData((prev) => ({
            ...prev,
            catagoryId: nextCategoryId,
            subCatagoryId: "",
            customSubCategory: "",
        }));
        setSubCatagories([]);
        setError("");

        if (!nextCategoryId) {
            return;
        }

        const selected = catagories.find((item) => item.Id === nextCategoryId);
        const categoryIsOther =
            selected && isOthersCategoryName(selected.name);
        if (categoryIsOther) {
            return;
        }

        const nestedChildren = sortCategoriesByName(getChildCategories(selected));
        if (nestedChildren.length > 0) {
            setSubCatagories(nestedChildren);
            return;
        }

        try {
            const data = await viewSubCatagoriesRef.current({ Id: nextCategoryId });
            if (data.err) {
                setError(data.err);
                return;
            }
            setSubCatagories(sortCategoriesByName(data.result || []));
        } catch (err) {
            setError("Unable to load sub categories right now.");
        }
    };

    const handleBackNavigation = () => {
        if (history.length > 1) {
            history.goBack();
            return;
        }
        history.push("/company/signin");
    };

    useEffect(() => {
        return () => {
            if (logoPreview) {
                URL.revokeObjectURL(logoPreview);
            }
        };
    }, [logoPreview]);

    useEffect(() => {
        const googlePrefill = location.state?.googlePrefill;
        if (!googlePrefill) {
            return;
        }

        setFormData((prev) => ({
            ...prev,
            email: prev.email || googlePrefill.email || "",
        }));
    }, [location.state]);

    useEffect(() => {
        let mounted = true;
        const loadMainCatagories = async () => {
            try {
                const data = await viewMainCatagoriesRef.current();
                if (!mounted) return;
                if (data.err) {
                    setError(data.err);
                    return;
                }
                setCatagories(sortCategoriesByName(data.result || []));
            } catch (err) {
                if (mounted) {
                    setError("Unable to load categories.");
                }
            }
        };

        loadMainCatagories();
        return () => {
            mounted = false;
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");

        try {
            if (!name || !email || !password || !city || !phone || !officePhone) {
                setError("Please fill in all required fields.");
                setLoading(false);
                return;
            }
            if (!catagoryId) {
                setError("Please select a category.");
                setLoading(false);
                return;
            }
            if (!isOtherCategory && subCatagories.length > 0 && !subCatagoryId) {
                setError("Please select a sub category.");
                setLoading(false);
                return;
            }
            if (isOtherCategory && !customSubCategory.trim()) {
                setError("Please specify your sub category.");
                setLoading(false);
                return;
            }
            if (password.length < 6) {
                setError("Password must be at least 6 characters.");
                setLoading(false);
                return;
            }
            if (password !== confirmPassword) {
                setError("Password and retype new password do not match.");
                setLoading(false);
                return;
            }
            if (!licenceFile) {
                setError("Please upload your business license.");
                setLoading(false);
                return;
            }

            const form = new FormData();
            form.append("name", name);
            form.append("description", description || "");
            form.append("email", email);
            form.append("password", password);
            form.append("web", web || "");
            form.append("pobox", pobox || "");
            form.append("woreda", woreda || "");
            form.append("city", city);
            form.append("subCity", subCity || "");
            form.append("state", state || "");
            form.append("kebele", kebele || "");
            form.append("street", street || "");
            form.append("phone", phone);
            form.append("officePhone", officePhone);
            form.append("fax", fax || "");
            const selectedCatagoryId =
                !isOtherCategory && subCatagoryId ? subCatagoryId : catagoryId;
            form.append("catagoryId", selectedCatagoryId);
            if (isOtherCategory) {
                form.append("customCategory", selectedMainCategory?.name || "Others");
                form.append("customSubCategory", customSubCategory.trim());
            }
            if (logoFile) {
                form.append("image", logoFile);
            }
            form.append("licence", licenceFile);

            const data = await signup(form);

            if (data.err) {
                setError(data.err);
                setLoading(false);
            } else {
                setSuccess(true);
                setLoading(false);
            }
        } catch (err) {
            setError("Something went wrong. Please try again.");
            setLoading(false);
        }
    };

    const handleGeneratePassword = () => {
        const next = generateStrongPassword(16);
        setGeneratedPassword(next);
        setError("");
    };

    const handleAcceptGeneratedPassword = () => {
        if (!generatedPassword) return;
        setFormData((prev) => ({
            ...prev,
            password: generatedPassword,
            confirmPassword: generatedPassword,
        }));
        setGeneratedPassword("");
    };

    const handleRejectGeneratedPassword = () => {
        setGeneratedPassword("");
    };

    if (success) {
        return (
            <div className={classes.root}>
                <div className={classes.container}>
                    <Card className={classes.card}>
                        <CardContent>
                            <Typography variant="h5" align="center" gutterBottom>
                                Request Submitted
                            </Typography>
                            <Box display="flex" justifyContent="center" my={2}>
                                <Avatar src={logoPreview} className={classes.companyAvatar}>
                                    <Business />
                                </Avatar>
                            </Box>
                            <Typography variant="body1" align="center" color="textSecondary">
                                Thanks, <strong>{name}</strong>. Your company signup request is pending admin approval.
                            </Typography>
                            <Typography variant="body2" align="center" color="textSecondary" style={{ marginTop: 8 }}>
                                You can sign in after your request is approved.
                            </Typography>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.root}>
            <div className={classes.container}>
                <div className={classes.logoContainer}>
                    <img src={logoImg} className={classes.logo} alt="Logo" />
                </div>

                <Card className={classes.card}>
                    <CardContent>
                        <Button
                            type="button"
                            startIcon={<ArrowBack />}
                            onClick={handleBackNavigation}
                            className={classes.backButton}
                        >
                            Back
                        </Button>
                        <Typography variant="h4" className={classes.title}>
                            Company Signup
                        </Typography>
                        <Typography variant="body2" className={classes.subtitle}>
                            Complete your registration to access the portal
                        </Typography>

                        {error && (
                            <Alert severity="error" style={{ marginBottom: 16 }}>
                                {error}
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit}>
                            <Box className={`${classes.formSection} ${classes.formGrid}`}>
                                <TextField
                                    fullWidth
                                    label="Company Name"
                                    variant="outlined"
                                    required
                                    value={name}
                                    onChange={handleChange("name")}
                                    className={`${classes.textField} ${classes.fullSpan}`}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Business color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    label="Description"
                                    variant="outlined"
                                    multiline
                                    minRows={3}
                                    value={description}
                                    onChange={handleChange("description")}
                                    className={`${classes.textField} ${classes.fullSpan}`}
                                />

                                <TextField
                                    fullWidth
                                    select
                                    label="Category"
                                    variant="outlined"
                                    required
                                    value={catagoryId}
                                    onChange={handleCategoryChange}
                                    className={classes.textField}
                                >
                                    <MenuItem value="">Select Category</MenuItem>
                                    {sortedCatagories.map((catagory) => (
                                        <MenuItem key={catagory.Id} value={catagory.Id}>
                                            {catagory.name}
                                        </MenuItem>
                                    ))}
                                </TextField>

                                {isOtherCategory ? (
                                    <>
                                        <TextField
                                            fullWidth
                                            label="Specify Sub Category"
                                            variant="outlined"
                                            required
                                            value={customSubCategory}
                                            onChange={handleChange("customSubCategory")}
                                            className={classes.textField}
                                        />
                                    </>
                                ) : subCatagories.length > 0 ? (
                                    <TextField
                                        fullWidth
                                        select
                                        label="Sub Category"
                                        variant="outlined"
                                        required
                                        value={subCatagoryId}
                                        onChange={handleChange("subCatagoryId")}
                                        className={classes.textField}
                                    >
                                        <MenuItem value="">Select Sub Category</MenuItem>
                                        {subCatagories.map((catagory) => (
                                            <MenuItem key={catagory.Id} value={catagory.Id}>
                                                {catagory.name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                ) : catagoryId ? (
                                    <TextField
                                        fullWidth
                                        label="Sub Category"
                                        variant="outlined"
                                        className={classes.textField}
                                        value="No sub category for selected category"
                                        InputProps={{ readOnly: true }}
                                    />
                                ) : null}

                                <TextField
                                    fullWidth
                                    label="Your Company Email Address"
                                    variant="outlined"
                                    required
                                    type="email"
                                    value={email}
                                    onChange={handleChange("email")}
                                    className={classes.textField}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Email color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    label="Personal Phone"
                                    variant="outlined"
                                    required
                                    value={phone}
                                    onChange={handleChange("phone")}
                                    className={classes.textField}
                                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                                />

                                <TextField
                                    fullWidth
                                    label="Office Phone"
                                    variant="outlined"
                                    required
                                    value={officePhone}
                                    onChange={handleChange("officePhone")}
                                    className={classes.textField}
                                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                                />

                                <TextField
                                    fullWidth
                                    label="Fax (optional)"
                                    variant="outlined"
                                    value={fax}
                                    onChange={handleChange("fax")}
                                    className={classes.textField}
                                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                                />

                                <TextField
                                    fullWidth
                                    label="Create a password"
                                    variant="outlined"
                                    required
                                    type="password"
                                    value={password}
                                    onChange={handleChange("password")}
                                    className={classes.textField}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    label="Retype new password"
                                    variant="outlined"
                                    required
                                    type="password"
                                    value={confirmPassword}
                                    onChange={handleChange("confirmPassword")}
                                    className={classes.textField}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Box className={`${classes.fullSpan} ${classes.passwordTools}`}>
                                    <button
                                        type="button"
                                        onClick={handleGeneratePassword}
                                        className={classes.miniButton}
                                    >
                                        Generate Password
                                    </button>
                                    {generatedPassword ? (
                                        <div className={classes.generatedBox}>
                                            <div>{generatedPassword}</div>
                                            <div className={classes.generatedActions}>
                                                <button
                                                    type="button"
                                                    onClick={handleAcceptGeneratedPassword}
                                                    className={classes.miniButton}
                                                >
                                                    Accept
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleRejectGeneratedPassword}
                                                    className={classes.miniButton}
                                                >
                                                    Reject
                                                </button>
                                            </div>
                                        </div>
                                    ) : null}
                                </Box>

                                <TextField
                                    fullWidth
                                    label="Website (optional)"
                                    variant="outlined"
                                    value={web}
                                    onChange={handleChange("web")}
                                    className={classes.textField}
                                    placeholder="rcndc.com or https://rcndc.com"
                                />

                                <TextField
                                    fullWidth
                                    label="City"
                                    variant="outlined"
                                    required
                                    value={city}
                                    onChange={handleChange("city")}
                                    className={classes.textField}
                                    inputProps={{ pattern: "[A-Za-z\\s]*" }}
                                />

                                <TextField
                                    fullWidth
                                    label="Sub City (optional)"
                                    variant="outlined"
                                    value={subCity}
                                    onChange={handleChange("subCity")}
                                    className={classes.textField}
                                    inputProps={{ pattern: "[A-Za-z\\s]*" }}
                                />

                                <TextField
                                    fullWidth
                                    label="State (optional)"
                                    variant="outlined"
                                    value={state}
                                    onChange={handleChange("state")}
                                    className={classes.textField}
                                    inputProps={{ pattern: "[A-Za-z\\s]*" }}
                                />

                                <TextField
                                    fullWidth
                                    label="Kebele (optional)"
                                    variant="outlined"
                                    value={kebele}
                                    onChange={handleChange("kebele")}
                                    className={classes.textField}
                                />

                                <TextField
                                    fullWidth
                                    label="Street (optional)"
                                    variant="outlined"
                                    value={street}
                                    onChange={handleChange("street")}
                                    className={classes.textField}
                                />

                                <TextField
                                    fullWidth
                                    label="P.O. Box (optional)"
                                    variant="outlined"
                                    value={pobox}
                                    onChange={handleChange("pobox")}
                                    className={classes.textField}
                                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                                />

                                <TextField
                                    fullWidth
                                    label="Woreda (optional)"
                                    variant="outlined"
                                    value={woreda}
                                    onChange={handleChange("woreda")}
                                    className={`${classes.textField} ${classes.fullSpan}`}
                                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                                />

                                <Box className={classes.companyDetail}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Upload Business License (PDF or Image)
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        color="primary"
                                    >
                                        {licenceName ? "Change License" : "Upload License"}
                                        <input
                                            type="file"
                                            accept="image/*,application/pdf"
                                            hidden
                                            required
                                            onChange={handleLicenceChange}
                                        />
                                    </Button>
                                    {licenceName && (
                                        <Typography variant="body2" color="textSecondary">
                                            {licenceName}
                                        </Typography>
                                    )}
                                </Box>

                                <Box className={classes.companyDetail}>
                                    <Avatar src={logoPreview || ""} className={classes.companyAvatar}>
                                        <Business />
                                    </Avatar>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        color="primary"
                                    >
                                        Upload Logo
                                        <input
                                            type="file"
                                            accept="image/*"
                                            hidden
                                            onChange={handleLogoChange}
                                        />
                                    </Button>
                                </Box>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={loading}
                                    className={`${classes.submitButton} ${classes.fullSpan}`}
                                >
                                    {loading ? "Registering..." : "Sign Up"}
                                </Button>
                                <Box mt={0} textAlign="center" className={classes.fullSpan}>
                                    <Typography variant="body2" color="textSecondary">
                                        Already have an account?{" "}
                                        <Link to="/company/signin" style={{ textDecoration: "none", color: "#667eea", fontWeight: "bold" }}>
                                            Sign In
                                        </Link>
                                    </Typography>
                                </Box>
                            </Box>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default withRouter(CompanySignup);
